import { authFetch, BASE_BACKEND_URL } from '../../auth'
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import useMediaQuery from '@mui/material/useMediaQuery'
import cookie from 'react-cookies'
import { getCurrentLocalDate, validateDateRange } from '../../helpers/formatDate'
import { layoutMobileMediaQuery } from '../../layout/layoutConstants'
import { ReportTabsMap, TAB_CONFIG, TAB_ENDPOINTS } from './helpers/reportTabs'
import TabsMui from '../../layout/TabsMui'
import AppListHeader from '../../components/title/AppListHeader'
import {
  EFFECTIVENESS_REPORT_TAB_VALUES,
  getVisibleEffectivenessReportTabs
} from '../../components/constants/effectivenessReportTabs'
import ReportDownloadDropdown from './components/ReportDownloadDropdown'
import UbiquaTowerToastAlert from '../../components/alerts/UbiquaTowerToastAlert'
import { EffectivenessIndexMobileLayout } from './mobile'
import { EFFECTIVENESS_MOBILE_USER_EXPANDABLE_CARD_FONT_SIZE } from './mobile/reportMobileKpiShared'
import ReportSummaryTab from './tab_components/ReportSummaryTab'
import ReportOrdersTab from './tab_components/ReportOrdersTab'
import ReportRefundsTab from './tab_components/ReportRefundsTab'
import ReportChargesTab from './tab_components/ReportChargesTab'
import ReportDeliveriesTab from './tab_components/ReportDeliveriesTab'
import ReportPosTab from './tab_components/ReportPosTab'
import SharedUserReportHeader from './components/SharedUserReportHeader'
import LoadingScreen from '../../components/LoadingScreen'
import { SETTLEMENT_STATUS_FILTER_OPTIONS } from './helpers/pendingSettlement'
import dayjs from '../../utils/dayjsConfig'

const PENDING_SETTLEMENT_FILTER_TABS = ['charges', 'deliveries']
const PER_PAGE = 25
const DEFAULT_PENDING_SETTLEMENT_FILTER = { mode: 'all', values: [] }

const buildPendingSettlementQuery = (filter) => {
  if (!filter || filter.mode === 'all' || !filter.values?.length) return ''
  return `&settlement_status=${encodeURIComponent(filter.values.join(','))}`
}

const buildDateRangeValue = (startDate, endDate) => {
  const start = dayjs(startDate)
  const end = dayjs(endDate)
  const fallback = dayjs(getCurrentLocalDate())

  return [start.isValid() ? start : fallback, end.isValid() ? end : fallback]
}

const TAB_COMPONENTS = {
  summary: ReportSummaryTab,
  orders: ReportOrdersTab,
  refunds: ReportRefundsTab,
  charges: ReportChargesTab,
  deliveries: ReportDeliveriesTab,
  pos: ReportPosTab
}

const EffectivenessIndexApp = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isMobileLayout = useMediaQuery(layoutMobileMediaQuery)
  const visibleTabs = useMemo(() => getVisibleEffectivenessReportTabs(), [])
  const defaultActiveTab = visibleTabs[0]?.value || EFFECTIVENESS_REPORT_TAB_VALUES.SUMMARY
  const restoredIndexState = location.state?.reportIndexState
  const requestedTab = searchParams.get('tab')
  const restoredActiveTab = visibleTabs.some(
    (tab) => tab.value === (requestedTab || restoredIndexState?.activeTab)
  )
    ? requestedTab || restoredIndexState.activeTab
    : defaultActiveTab
  const restoredStartDate =
    searchParams.get('start_date') || restoredIndexState?.startDate || getCurrentLocalDate()
  const restoredEndDate =
    searchParams.get('end_date') || restoredIndexState?.endDate || getCurrentLocalDate()
  const requestedPage = Number(searchParams.get('page'))
  const requestedSettlementStatuses = searchParams
    .get('settlement_status')
    ?.split(',')
    .filter((value) => SETTLEMENT_STATUS_FILTER_OPTIONS.some((option) => option.value === value))
  const restoredPendingSettlementFilter = requestedSettlementStatuses?.length
    ? { mode: 'custom', values: requestedSettlementStatuses }
    : restoredIndexState?.pendingSettlementFilter || DEFAULT_PENDING_SETTLEMENT_FILTER
  const [activeTab, setActiveTab] = useState(restoredActiveTab)
  const endpoint = TAB_ENDPOINTS[activeTab] || TAB_ENDPOINTS[defaultActiveTab]

  const selectedTab = ReportTabsMap[activeTab]
  const ActiveComponent = TAB_COMPONENTS[activeTab]
  const showPendingSettlementFilter = PENDING_SETTLEMENT_FILTER_TABS.includes(activeTab)

  const [startDate, setStartDate] = useState(restoredStartDate)
  const [endDate, setEndDate] = useState(restoredEndDate)
  const isSingleDayRange = startDate === endDate
  const [dateRangeDraft, setDateRangeDraft] = useState(() =>
    buildDateRangeValue(restoredStartDate, restoredEndDate)
  )
  const [loadErrors, setLoadErrors] = useState({ table: false, cards: false })
  const hasError = loadErrors.table || loadErrors.cards
  const [notice, setNotice] = useState(null)
  const noticeId = useRef(0)

  const [currentPage, setCurrentPage] = useState(
    Number.isInteger(requestedPage) && requestedPage > 0
      ? requestedPage
      : restoredIndexState?.currentPage || 1
  )
  const requestedPerPage = Number(
    searchParams.get('per_page') || restoredIndexState?.perPage || PER_PAGE
  )
  const [perPage, setPerPage] = useState(
    [10, 25, 50].includes(requestedPerPage) ? requestedPerPage : PER_PAGE
  )
  const [loadedTotalCount, setTotalCount] = useState(0)
  const totalCount = hasError ? 0 : loadedTotalCount

  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('search') || restoredIndexState?.searchQuery || ''
  )
  const [pendingSettlementFilter, setPendingSettlementFilter] = useState(
    restoredPendingSettlementFilter
  )

  const [isSummaryFetching, setIsSummaryFetching] = useState(false)
  const [isCardFetching, setIsCardFetching] = useState(false)
  const [isFullReportFetching, setIsFullReportFetching] = useState(true)
  const isFetching = isSummaryFetching || isCardFetching
  const [loadedData, setData] = useState([])
  const [loadedKpiData, setKpiData] = useState([])
  const data = hasError ? [] : loadedData
  const kpiData = hasError ? [] : loadedKpiData

  const abortSummaryRef = useRef(null)
  const abortCardRef = useRef(null)
  const summaryRequestIdRef = useRef(0)
  const cardRequestIdRef = useRef(0)
  const cardsPeriodRef = useRef(null)

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.value === activeTab)) {
      setActiveTab(defaultActiveTab)
    }
  }, [activeTab, defaultActiveTab, visibleTabs])

  useEffect(() => {
    const nextParams = new URLSearchParams()
    if (activeTab !== defaultActiveTab) nextParams.set('tab', activeTab)
    nextParams.set('start_date', startDate)
    nextParams.set('end_date', endDate)
    if (currentPage > 1) nextParams.set('page', String(currentPage))
    if (perPage !== PER_PAGE) nextParams.set('per_page', String(perPage))
    if (searchQuery) nextParams.set('search', searchQuery)
    if (pendingSettlementFilter.mode !== 'all' && pendingSettlementFilter.values?.length) {
      nextParams.set('settlement_status', pendingSettlementFilter.values.join(','))
    }

    if (nextParams.toString() !== searchParams.toString()) {
      navigate({ search: nextParams.toString() }, { replace: true })
    }
  }, [
    activeTab,
    currentPage,
    perPage,
    defaultActiveTab,
    endDate,
    navigate,
    pendingSettlementFilter,
    searchParams,
    searchQuery,
    startDate
  ])

  useEffect(() => {
    const handleBeforeUnload = () => {
      abortSummaryRef.current?.abort('Page unload')
      abortCardRef.current?.abort('Page unload')
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      summaryRequestIdRef.current += 1
      cardRequestIdRef.current += 1
      abortSummaryRef.current?.abort()
      abortCardRef.current?.abort()
    }
  }, [])

  const notifyError = (message, source = 'request') =>
    setNotice({
      id: `report-notice-${++noticeId.current}`,
      message,
      source
    })
  const applyValidationResult = (result) => {
    const message = result.invalidRangeOffset
      ? 'No se pueden seleccionar fechas posteriores a hoy.'
      : result.invalidRangeLimit
        ? 'El rango de fechas no puede superar los 12 meses.'
        : result.invalidRangeFormat
          ? 'La fecha de inicio debe ser anterior a la fecha de fin.'
          : result.invalidDateFormat
            ? 'El formato de las fechas no es válido.'
            : null
    if (message) notifyError(message, 'dates')
    else setNotice((current) => (current?.source === 'dates' ? null : current))
  }

  const validateDates = () => {
    const result = validateDateRange(startDate, endDate)
    applyValidationResult(result)
    return result.isValid
  }

  const dateRangeValue = useMemo(
    () => buildDateRangeValue(startDate, endDate),
    [endDate, startDate]
  )

  useEffect(() => {
    setDateRangeDraft(dateRangeValue)
  }, [dateRangeValue])

  const resetPendingSettlementFilter = () => {
    setPendingSettlementFilter(DEFAULT_PENDING_SETTLEMENT_FILTER)
  }

  const pendingSettlementQuery = useMemo(
    () =>
      showPendingSettlementFilter && isSingleDayRange
        ? buildPendingSettlementQuery(pendingSettlementFilter)
        : '',
    [isSingleDayRange, pendingSettlementFilter, showPendingSettlementFilter]
  )

  const applyTablePayload = (payload) => {
    const totalRecords = Math.max(0, Number(payload.total_count) || 0)
    const validPage = Math.min(currentPage, Math.max(1, Math.ceil(totalRecords / perPage)))
    if (validPage !== currentPage) {
      setCurrentPage(validPage)
      return
    }
    setData(payload.table || [])
    setTotalCount(totalRecords)
  }

  const fetchDataSummary = async (query = '', settlementQuery = pendingSettlementQuery) => {
    if (abortSummaryRef.current) {
      abortSummaryRef.current.abort()
    }

    const controller = new AbortController()
    abortSummaryRef.current = controller
    const requestId = summaryRequestIdRef.current + 1
    summaryRequestIdRef.current = requestId

    setIsSummaryFetching(true)
    setLoadErrors((current) => ({ ...current, table: false }))

    try {
      const res = await authFetch(
        `${BASE_BACKEND_URL}/reports/user_reports/presentation/${endpoint}?start_date=${startDate}&end_date=${endDate}&page=${currentPage}&per_page=${perPage}&search=${encodeURIComponent(query)}${settlementQuery}`,
        {
          method: 'GET',
          signal: controller.signal
        }
      )

      if (!res) return false

      if (!res.ok) {
        throw new Error(`HTTP error ${res.status}`)
      }

      const data = await res.json()
      if (requestId !== summaryRequestIdRef.current) return false

      applyTablePayload(data)
      return true
    } catch (err) {
      if (requestId !== summaryRequestIdRef.current) return false

      if (err.name !== 'AbortError') {
        console.error('Error fetchDataSummary:', err)
        setLoadErrors((current) => ({ ...current, table: true }))
        notifyError('Hubo un error al traer los datos. Intenta nuevamente.')
        setData([])
        setTotalCount(0)
      }
      return false
    } finally {
      if (requestId === summaryRequestIdRef.current) {
        setIsSummaryFetching(false)
      }
    }
  }

  const fetchCardReportData = async () => {
    if (abortCardRef.current) {
      abortCardRef.current.abort()
    }

    const controller = new AbortController()
    abortCardRef.current = controller
    const requestId = cardRequestIdRef.current + 1
    cardRequestIdRef.current = requestId

    setIsCardFetching(true)
    setLoadErrors((current) => ({ ...current, cards: false }))

    try {
      const res = await authFetch(
        `${BASE_BACKEND_URL}/reports/user_reports/presentation/${endpoint}?start_date=${startDate}&end_date=${endDate}&page=${currentPage}&per_page=${perPage}&card_reports=true`,
        {
          method: 'GET',
          signal: controller.signal
        }
      )

      if (!res || controller.signal.aborted) return false

      if (!res.ok) {
        throw new Error(`HTTP error ${res.status}`)
      }

      const data = await res.json()
      if (requestId !== cardRequestIdRef.current || controller.signal.aborted) return false

      setKpiData(data.cards_summary || [])
      return true
    } catch (err) {
      if (requestId !== cardRequestIdRef.current || controller.signal.aborted) return false

      if (err.name !== 'AbortError') {
        console.error('Error fetchCardReportData:', err)
        setLoadErrors((current) => ({ ...current, cards: true }))
        notifyError('Hubo un error al traer los datos. Intenta nuevamente.')
        setKpiData([])
      }
      return false
    } finally {
      if (requestId === cardRequestIdRef.current) {
        setIsCardFetching(false)
      }
    }
  }

  const totalPages = Math.ceil(totalCount / perPage)

  const handleDateRangeChange = (dates) => {
    if (!Array.isArray(dates) || dates.length === 0) {
      setDateRangeDraft(dateRangeValue)
      return
    }

    const sortedDates = [...dates].sort((a, b) => a.valueOf() - b.valueOf())
    setDateRangeDraft(sortedDates)

    if (sortedDates.length < 2) return

    const nextStart = sortedDates[0]
    const nextEnd = sortedDates[sortedDates.length - 1]
    const nextStartDate = nextStart.format('YYYY-MM-DD')
    const nextEndDate = nextEnd.format('YYYY-MM-DD')

    setCurrentPage(1)
    if (nextStartDate !== nextEndDate) resetPendingSettlementFilter()
    setStartDate(nextStartDate)
    setEndDate(nextEndDate)
  }

  const handleTabChange = (tabKey) => {
    setSearchQuery('')
    setPendingSettlementFilter(DEFAULT_PENDING_SETTLEMENT_FILTER)
    setCurrentPage(1)
    setData([])
    setKpiData([])
    setActiveTab(tabKey)
  }

  const handlePendingSettlementFilterChange = (nextFilter) => {
    setPendingSettlementFilter(nextFilter || DEFAULT_PENDING_SETTLEMENT_FILTER)
    setCurrentPage(1)
    setData([])
    setTotalCount(0)
  }

  const handlePaginationClick = (event) => {
    const target = event.target.tagName === 'A' ? event.target : event.target.closest('a')
    if (!target) return

    let newPage = currentPage
    switch (target.id) {
      case 'first-page':
        newPage = 1
        break
      case 'last-page':
        newPage = totalPages
        break
      case 'previous-page':
        if (currentPage > 1) newPage = currentPage - 1
        break
      case 'next-page':
        if (currentPage < totalPages) newPage = currentPage + 1
        break
      default:
        return
    }

    if (newPage !== currentPage) {
      setCurrentPage(newPage)
    }
  }

  const handlePaginationPageChange = (newPage) => {
    if (!newPage || newPage === currentPage) return
    setCurrentPage(newPage)
  }

  useEffect(() => {
    let cancelled = false
    const periodKey = `${activeTab}|${startDate}|${endDate}`
    const shouldRefreshCards = cardsPeriodRef.current !== periodKey

    const loadTableThenCards = async () => {
      setIsFullReportFetching(shouldRefreshCards)
      try {
        if (!validateDates()) return
        const tableLoaded = await fetchDataSummary(searchQuery, pendingSettlementQuery)
        if (!cancelled && tableLoaded && shouldRefreshCards) {
          const cardsLoaded = await fetchCardReportData()
          if (!cancelled && cardsLoaded) cardsPeriodRef.current = periodKey
        }
      } finally {
        if (!cancelled) setIsFullReportFetching(false)
      }
    }

    loadTableThenCards()
    return () => {
      cancelled = true
    }
  }, [activeTab, currentPage, perPage, searchQuery, startDate, endDate, pendingSettlementQuery])

  const showModel = (event, user_db_ref) => {
    event.preventDefault()
    navigate(
      `/usuario_reportes?user_db_ref=${encodeURIComponent(user_db_ref)}&start_date=${startDate}&end_date=${endDate}`,
      {
        state: {
          reportIndexState: {
            activeTab,
            startDate,
            endDate,
            currentPage,
            perPage,
            searchQuery,
            pendingSettlementFilter
          }
        }
      }
    )
  }

  const activeTabTitle = TAB_CONFIG.find((t) => t.key === activeTab)?.label

  const searchFromDates = (draftStart, draftEnd) => {
    setCurrentPage(1)
    if (draftStart !== undefined && draftEnd !== undefined) {
      const result = validateDateRange(draftStart, draftEnd)
      applyValidationResult(result)
      if (draftStart !== draftEnd) resetPendingSettlementFilter()
      setStartDate(draftStart)
      setEndDate(draftEnd)
      return
    }
    validateDates()
  }

  const handleSearchQuery = (query) => {
    setSearchQuery(query)
    setCurrentPage(1)
  }

  const handleResetFilters = () => {
    setPendingSettlementFilter(DEFAULT_PENDING_SETTLEMENT_FILTER)
    setSearchQuery('')
    setCurrentPage(1)
  }

  const hasActiveSettlementFilter =
    pendingSettlementFilter?.mode !== 'all' && pendingSettlementFilter?.values?.length > 0
  const shouldShowReset =
    showPendingSettlementFilter && (hasActiveSettlementFilter || Boolean(searchQuery))
  // Only lock the filter on multi-day ranges. Empty table results must not
  // disable it, or a selected status never gets a chance to re-fetch.
  const isSettlementFilterDisabled = !isSingleDayRange

  const retryReport = async () => {
    if (!validateDates()) return
    setNotice(null)
    setIsFullReportFetching(true)
    try {
      const tableLoaded = await fetchDataSummary(searchQuery, pendingSettlementQuery)
      if (tableLoaded) {
        const cardsLoaded = await fetchCardReportData()
        if (cardsLoaded) cardsPeriodRef.current = `${activeTab}|${startDate}|${endDate}`
      }
    } finally {
      setIsFullReportFetching(false)
    }
  }

  const tabBodyProps = {
    activeTab,
    kpiData,
    cardsLoading: isCardFetching,
    cardsLoadError: loadErrors.cards,
    onRetryCards: retryReport,
    tableLoading: isSummaryFetching,
    data,
    currentPage,
    totalPages,
    totalCount,
    perPage,
    startDate,
    endDate,
    hasPendingSettlementQuery: pendingSettlementQuery,
    onPageChange: (_, page) => setCurrentPage(page),
    onRowsPerPageChange: (_, size) => {
      setPerPage(Number(size))
      setCurrentPage(1)
    },
    searchQuery,
    onSearchQuery: handleSearchQuery,
    onRowClick: showModel,
    pendingSettlementFilter,
    onPendingSettlementFilterChange: handlePendingSettlementFilterChange,
    isSingleDayRange,
    shouldShowReset,
    onResetFilters: handleResetFilters
  }

  useEffect(() => {
    if (!hasError && !isFetching) {
      setNotice((current) => (current?.source === 'request' ? null : current))
    }
  }, [hasError, isFetching])

  return (
    <div
      className={`app-list-shell ubiqua-type report-index-shell${
        isMobileLayout ? '' : ' report-desktop'
      }`}
    >
      <UbiquaTowerToastAlert
        open={Boolean(notice)}
        id={notice?.id}
        severity="error"
        title={notice?.source === 'dates' ? 'Revisa las fechas' : 'No se pudo cargar el reporte'}
        message={notice?.message}
        durationMs={null}
        showCountdown={false}
        action={
          notice?.source === 'request'
            ? { label: 'Intentar de nuevo', onClick: retryReport }
            : undefined
        }
        onClose={() => setNotice(null)}
      />
      <AppListHeader
        title="Reporte de usuarios"
        subtitle="Actividad de tus usuarios App en el periodo seleccionado"
      />
      <div className="index-table app-list-content">
        <div className="show-area">
          {isMobileLayout ? (
            <EffectivenessIndexMobileLayout
              activeTab={activeTab}
              tabs={visibleTabs}
              setActiveTab={handleTabChange}
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              today={getCurrentLocalDate()}
              searchFromDates={searchFromDates}
              isFetching={isFetching}
              selectedTab={selectedTab}
              activeTabTitle={activeTabTitle}
              kpiData={kpiData}
              data={data}
              currentPage={currentPage}
              totalPages={totalPages}
              onPaginationPageChange={handlePaginationPageChange}
              handlePaginationClick={handlePaginationClick}
              searchQuery={searchQuery}
              onSearchQuery={handleSearchQuery}
              onRowClick={showModel}
              userExpandableCardFontSize={EFFECTIVENESS_MOBILE_USER_EXPANDABLE_CARD_FONT_SIZE}
              hasPendingSettlementQuery={pendingSettlementQuery}
              isSingleDayRange={isSingleDayRange}
              showSettlementFilter={showPendingSettlementFilter}
              pendingSettlementFilter={pendingSettlementFilter}
              onPendingSettlementFilterChange={handlePendingSettlementFilterChange}
              isSettlementFilterDisabled={isSettlementFilterDisabled}
            />
          ) : (
            <>
              <TabsMui
                tabs={visibleTabs}
                activeTab={activeTab}
                onChange={handleTabChange}
                scrollable
                appearance="pill"
              />

              {isFullReportFetching || isCardFetching ? (
                <LoadingScreen text="Cargando..." minHeight="calc(100dvh - 220px)" />
              ) : (
                <>
                  <SharedUserReportHeader
                    compactPeriodNote
                    dateRangeValue={dateRangeDraft}
                    onDateRangeChange={handleDateRangeChange}
                    downloadContent={
                      <ReportDownloadDropdown
                        disabled={isCardFetching}
                        activeTab={activeTab}
                        startDate={startDate}
                        endDate={endDate}
                        searchQuery={searchQuery}
                        hasPendingSettlementQuery={pendingSettlementQuery}
                      />
                    }
                    noteMessage="Solo puedes escoger un rango de fechas máximo de 12 meses."
                  />
                  <div className="mt-30">
                    <ActiveComponent {...tabBodyProps} />
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default EffectivenessIndexApp
